import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { BRANDS, BRAND_REFERENCE } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildBrandRelationParams } from './brandApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('brand_category_id', values.brand_category_id);
  fd.append('name', values.name ?? '');
  fd.append('type', values.type ?? '');
  fd.append('url', values.url ?? '');
  fd.append('address', values.address ?? '');
  fd.append('invoice_limit', String(values.invoice_limit ?? ''));
  fd.append('status', Number(values.status ?? 1));
  return fd;
};

const categoryLabel = (c) => c.name ?? c.title ?? String(c.id);

const CreateBrand = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [refLoading, setRefLoading] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [url, setUrl] = useState('');
  const [address, setAddress] = useState('');
  const [invoiceLimit, setInvoiceLimit] = useState('');
  const [brandRecord, setBrandRecord] = useState(null);

  const fetchReference = useCallback(async () => {
    setRefLoading(true);
    try {
      const [catRes] = await Promise.allSettled([
        api.get(BRAND_REFERENCE.CATEGORIES, { params: { paginate: 0 } }),
      ]);

      if (catRes.status === 'fulfilled') {
        const list = extractList(catRes.value.data);
        const arr = Array.isArray(list) ? list : [];
        setCategoryOptions(
          arr.map((c) => ({
            id: c.id,
            label: categoryLabel(c),
            raw: c,
          }))
        );
      } else {
        toast.warn('Could not load brand categories — check /brand-categories');
        setCategoryOptions([]);
      }
    } finally {
      setRefLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReference();
  }, [fetchReference]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setBrandRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(BRANDS.BY_ID(editId));
        const b = data?.data ?? data;
        if (cancelled || !b) return;
        setBrandRecord(b);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load brand');
        navigate('/brands');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !brandRecord) return;
    const b = brandRecord;

    setName(b.name ?? '');
    setType(b.type ?? '');
    setUrl(b.url ?? '');
    setAddress(b.address ?? '');
    setInvoiceLimit(b.invoice_limit != null ? String(b.invoice_limit) : '');

    const nestedCategory =
      (b.brand_category && typeof b.brand_category === 'object' ? b.brand_category : null) ??
      (b.brandCategory && typeof b.brandCategory === 'object' ? b.brandCategory : null);

    const cid =
      b.brand_category_id ?? nestedCategory?.id ?? b.brandCategory?.id ?? undefined;

    if (cid) {
      if (nestedCategory) {
        const opt = { id: cid, label: categoryLabel(nestedCategory), raw: nestedCategory };
        setSelectedCategory(opt);
        setCategoryOptions((prev) =>
          prev.some((o) => o.id === cid) ? prev : [...prev, opt]
        );
      } else {
        const opt = categoryOptions.find((o) => o.id === cid);
        setSelectedCategory(opt ?? { id: cid, label: String(cid), raw: {} });
      }
    } else {
      setSelectedCategory(null);
    }
  }, [isEdit, brandRecord, categoryOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCategory?.id || !name.trim()) {
      toast.warn('Select brand category and enter a name');
      return;
    }
    if (!type.trim() || !url.trim()) {
      toast.warn('Type and URL are required');
      return;
    }
    if (invoiceLimit === '' || Number.isNaN(Number(invoiceLimit))) {
      toast.warn('Enter a valid invoice limit');
      return;
    }

    const values = {
      brand_category_id: selectedCategory.id,
      name: name.trim(),
      type: type.trim(),
      url: url.trim(),
      address: address.trim(),
      invoice_limit: invoiceLimit,
      status: 1,
    };

    const fd = buildFormData(values);
    const q = new URLSearchParams(buildBrandRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${BRANDS.BY_ID(editId)}?${q}`, fd);
        toast.success('Brand updated');
      } else {
        await api.post(`${BRANDS.LIST}`, fd);
        toast.success('Brand created');
      }
      navigate('/brands');
    } catch (err) {
      toast.error(
        err.response?.data?.message || (isEdit ? 'Failed to update brand' : 'Failed to create brand')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/brands')} sx={{ mb: 2 }}>
          Back to brands
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit brand' : 'Create brand'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={(_, v) => setSelectedCategory(v)}
                  loading={refLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Brand category" required fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  required
                  fullWidth
                  placeholder="e.g. ppc, seo"
                  helperText="Examples: ppc, seo"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="URL"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  fullWidth
                  type="url"
                  placeholder="https://"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Invoice limit"
                  value={invoiceLimit}
                  onChange={(e) => setInvoiceLimit(e.target.value)}
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create brand'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/brands')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateBrand;
